import { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
} from '@mui/material';

import {
  RebalanceItem,
  rejectRebalance,
} from 'src/pages/admin/services/xbitRelayRebalancesService';

import { RebalanceSummary } from './RebalanceSummary';
import { WalletSignerPicker, useWalletSigner } from './WalletSigner';
import { buildRejectRebalanceMessage } from './MessageBuilders';
import { errorMessage, isUserRejection } from './helpers';

type RejectDialogProps = {
  record: RebalanceItem;
  onClose(): void;
};

export const RejectDialog = ({ record, onClose }: RejectDialogProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const signer = useWalletSigner();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasonEmpty = !reason.trim();
  const busy = submitting || signer.connecting;

  const handleSignAndReject = async () => {
    if (!signer.address || reasonEmpty) return;
    setSubmitting(true);
    try {
      // `reason` goes in the body for the audit trail but is not part of the signed message —
      // only the id, the action and the timestamp are.
      const trimmedReason = reason.trim();
      const timestampMs = Date.now();

      const signature = await signer.signMessage(
        buildRejectRebalanceMessage({ id: record.id, timestampMs })
      );

      const { data } = await rejectRebalance(record.id, {
        admin_id: signer.address.toLowerCase(),
        reason: trimmedReason,
        timestamp_ms: timestampMs,
        signature,
      });

      // Business failures arrive as HTTP 200 with success: false.
      if (!data?.success) {
        notify(data?.message || t('module.xbitRelayHotWalletBalancer.rejectRejected'), {
          type: 'error',
        });
        refresh();
        return;
      }

      notify('module.xbitRelayHotWalletBalancer.rejectSuccess', { type: 'success' });
      onClose();
      refresh();
    } catch (error) {
      if (isUserRejection(error)) return;
      notify(errorMessage(error, t('module.xbitRelayHotWalletBalancer.rejectRejected')), {
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayHotWalletBalancer.rejectTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <RebalanceSummary record={record} />

          <Divider />

          <TextField
            label={t('module.xbitRelayHotWalletBalancer.rejectReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            error={reasonEmpty}
            helperText={t('module.xbitRelayHotWalletBalancer.reasonSignedHelp')}
            disabled={busy}
          />

          <Divider />

          <WalletSignerPicker signer={signer} busy={submitting} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={busy}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSignAndReject}
          disabled={!signer.address || busy || reasonEmpty}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayHotWalletBalancer.signAndReject')}
        </Button>
      </DialogActions>
    </>
  );
};
